import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  Modal,
  Tag,
  Avatar,
  notification,
  Tooltip,
  Popconfirm,
  Button,
  DatePicker,
  InputNumber,
  Switch,
  Table
} from 'antd';
import { Comment } from '@ant-design/compatible';
import {
  reloadLayawayBinsAtom,
  selectedLayawayAtom,
  reloadLayawaysAtom,
  timezoneAtom,
  selectedOrderAtom,
  viewDetailsAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import OrderDetailsModal from '../../../shared-components/modals/OrderDetailsModal';
import Grocefy from '../../../../assets/images/grocefyLogoAlone.png';
import envConfig from '../../../../envConfig';
import { formatPhoneNumber, mapOrderStatus, mapOrderType, mapPaymentType, renderDate, renderShortDate, renderShortDateHour, renderShortDateTime, thousand, toCurrency } from '../../../utils/functions';

function LayawayModal() {
  const { t } = useTranslation();
  const timezone = useRecoilValue(timezoneAtom);
  const [viewDetails, setViewDetails] = useRecoilState(viewDetailsAtom);
  const setSelectedOrder = useSetRecoilState(selectedOrderAtom);
  const setReloadLayawayBins = useSetRecoilState(reloadLayawayBinsAtom);
  const setReloadLayaways = useSetRecoilState(reloadLayawaysAtom);
  const [selectedLayaway, setSelectedLayaway] = useRecoilState(selectedLayawayAtom);
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [data, setData] = useState(null);
  const [overdue, setOverdue] = useState(false);
  const [overdueTomorrow, setOverdueTomorrow] = useState(false);

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function updateLayaway() {
    setLoading(true);
    api.put('layaway', {
      ...data,
      forceExpirationDate: data?.forceExpirationDate ? dayjs(data?.forceExpirationDate).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour').startOf('day') : null
    })
      .then((response) => {
        if (response.data.success) {
          setData(response.data.data);
          setReloadLayaways(true);
          if (response.data.message) {
            showMessage(response.data.message);
          }
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        showMessage(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function assignNewBin() {
    setLoading(true);
    api.post('layaway/bins/assign', {
      LayawayId: selectedLayaway?.id
    })
      .then((response) => {
        if (response.data.success) {
          setSelectedLayaway({
            ...selectedLayaway,
            layawayBins: response.data.data.results
          });
          setReloadLayaways(true);
          setReloadLayawayBins(true);
          if (response.data.message) {
            showMessage(response.data.message);
          }
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        showMessage(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function freeupBin(id) {
    setLoading(true);
    api.post(`layaway/bins/freeup/${id}`, null)
      .then((response) => {
        if (response.data.success) {
          setSelectedLayaway({
            ...selectedLayaway,
            layawayBins: response.data.data.results
          });
          setReloadLayaways(true);
          setReloadLayawayBins(true);
          if (response.data.message) {
            showMessage(response.data.message);
          }
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        showMessage(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function removeItem(id) {
    // todo
  }

  function getOrder(orderId) {
    setLoadingOrder(true);
    api.get(`orders/order/${orderId}`)
      .then((response) => {
        setLoadingOrder(false);
        if (response.data.error) {
          showMessage(response.data.error);
        } else {
          const order = response.data.data;
          setSelectedOrder({
            ...order,
            key: order.id,
            id: order.id,
            itemCount: order.itemCount,
            storeId: order.businessId,
            locationId: order.locationId,
            orderType: order.type,
            deliveryZone: order.deliveryZoneName,
            createdAt: renderShortDateTime(order.actualTime, timezone),
            cancelledAt: renderShortDateTime(order.cancellationDate, timezone),
            createdDiff: moment(`${order.createdAt}+0000`).tz(timezone).fromNow(),
            dispatchDiff:
              order.dispatchDateTime == null
                ? 'N/A'
                : moment(`${order.dispatchDateTime}+0000`).tz(timezone).fromNow(),
            businessName: order.businessName,
            type: t(mapOrderType(order.type)),
            typeEnum: order.type,
            confirmationCode: order?.confirmationCode,
            customer: order.customerName,
            customerId: order.customerId,
            customerPhone: formatPhoneNumber(order.customerPhone),
            customerLocationId: order.customerLocationId,
            ivuCN: order.ivuControlNumber === null ? 'N/A' : order.ivuControlNumber,
            statusText: mapOrderStatus(order.status),
            cardNickname: order.cardNickname,
            nickName: order.nickName,
            cardholderName: order.cardholderName,
            status: order.status,
            paymentTypeText: t(mapPaymentType(order.paymentType)),
            paymentType: order.paymentType,
            desiredDateTime: renderShortDateHour(order.desiredDateTime, timezone),
            date: renderShortDateHour(order.date, timezone),
            dDateTime: order.desiredDateTime,
            dispatchDateTime: renderDate(order.dispatchDateTime, timezone),
            total: order.orderTotal,
            orderTotal: order.orderTotal,
            subTotal: order.productsTotal,
            munTax: order.municipalTax,
            stateTax: order.stateTax,
            deliveryFee: order.deliveryTotal,
            pickupFee: order.processingFee,
            transferTotal: order.transferTotal,
            processingFee: order.processingFee,
            transferCompletedText: order.transferCompleted ? 'Yes' : 'No',
            transferCompleted: order.transferCompleted,
            authTotal: order.authTotal,
            returnTotal: order.returnTotal,
            credit:
              order.authTotal - order.orderTotal
                ? (order.authTotal - order.orderTotal)
                : '0.00',
            referenceNumber:
              order.referenceNumber || order.referenceNumber !== ''
                ? order.referenceNumber
                : 'n/a',
            refNumAvailable: order.referenceNumber !== '' && order.referenceNumber !== null,
            discountAmount: order.discountAmount,
            businessDescription: order.businessDescription,
            returnNeeded: order.returnNeeded ? 'Yes' : 'No',
            returnCompleted: order.returnCompleted ? 'Yes' : 'No',
            athReturnError: order.athReturnError ? 'Yes' : 'No',
            wasAlreadyCharged: order.hasBeenCharged ? 'Yes' : 'No',
            wasAlreadyAuthed: order.hasBeenAuthed ? 'Yes' : 'No',
            hasBeenCharged: order.hasBeenCharged,
            hasBeenAuthed: order.hasBeenAuthed,
            items: order.items,
            signatureImageUrl: order.signatureImageUrl,
            ebtReceiptUrl: order.ebtReceiptUrl,
            pickUpName: order.pickUpName,
            customerLocation: order.customerLocation,
            paymentTender: order.paymentTender,
            posLaneTag: order.posLaneTag,
          });
          setViewDetails(true);
        }
      })
      .catch((error) => {
        showMessage(error.message);
      })
      .finally(() => {
        setLoadingOrder(false);
      });
  }

  function getPaidAmount() {
    let paidAmount = 0;
    data?.layawayPayments.forEach((payment) => {
      paidAmount += payment.total;
    });
    return paidAmount;
  }

  function getPayoffAmount() {
    const paidAmount = getPaidAmount();
    return data?.total - paidAmount;
  }

  useEffect(() => {
    setData(selectedLayaway);
    if (selectedLayaway) {
      let offset = envConfig.REACT_APP_TIMEZONE_OFFSET;
      if (!Number.isInteger(offset)) {
        offset = parseInt(offset, 10);
      }
      const now = dayjs(dayjs().format('YYYY-MM-DD')).add(-1 * offset, 'hours');
      const tomorrow = now.add(1, 'days');
      const diffNow = now.diff(selectedLayaway?.nextExpirationDate, 'days');
      const diffTomorrow = tomorrow.diff(selectedLayaway?.nextExpirationDate, 'days');
      if (diffNow === 0) {
        setOverdueTomorrow(true);
      } else if (diffTomorrow > 0) {
        setOverdue(true);
      }
    } else {
      setOverdueTomorrow(false);
      setOverdue(false);
    }
  }, [selectedLayaway]);

  useEffect(() => {
    if (!viewDetails) {
      setSelectedOrder(null);
    }
  }, [viewDetails]);

  return (
    <>
      <Modal
        title={(
          <div>
            <Tag>{selectedLayaway?.code}</Tag>
            {overdueTomorrow && (
              <Tag color="orange">{t('overdue_tomorrow')}</Tag>
            )}
            {overdue && (
              <Tag color="red">{t('overdue')}</Tag>
            )}
          </div>
        )}
        open={selectedLayaway}
        onCancel={() => setSelectedLayaway(null)}
        footer={[
          <Popconfirm
            title={t('are_you_sure_assign_bin_prompt')}
            okText={t('yes')}
            cancelText={t('no')}
            onConfirm={() => {
              assignNewBin();
            }}
          >
            <Button
              type="primary"
              loading={loading}
            >
              {t('assign_new_bin')}
            </Button>
          </Popconfirm>,
          <Popconfirm
            title={t('are_you_sure_update_layaway_prompt')}
            okText={t('yes')}
            cancelText={t('yes')}
            onConfirm={() => {
              updateLayaway();
            }}
          >
            <Button
              type="primary"
              loading={loading}
            >
              {t('update')}
            </Button>
          </Popconfirm>
        ]}
        width="100%"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="col-span-1">
            <strong>{t('last_payment_on')}: </strong><span>{data?.lastPaymentDate ? renderShortDateTime(data?.lastPaymentDate, timezone) : '-'}</span>
          </div>
          <div className="col-span-1">
            <strong>{t('remind_user_for_payments')}</strong>
            <Switch
              className="ml-2"
              disabled={loading}
              checked={data?.userPaymentReminder}
              onChange={(obj) => setData({
                ...data,
                userPaymentReminder: obj
              })}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('days_between_payments')}</p>
            <InputNumber
              className="w-full"
              disabled={loading}
              min={0}
              step={1}
              onChange={(obj) => setData({
                ...data,
                daysForExpiration: obj
              })}
              value={data?.daysForExpiration}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('force_expire_on')}</p>
            <DatePicker
              className="w-full"
              disabled={loading}
              format="MMM DD, YYYY"
              allowClear
              onChange={(obj) => setData({
                ...data,
                forceExpirationDate: obj
              })}
              value={data?.forceExpirationDate ? dayjs(data?.forceExpirationDate).add(envConfig.REACT_APP_TIMEZONE_OFFSET, 'hour') : null}
            />
          </div>
          <div className="col-span-1 lg:col-span-3">
            <p className="font-bold">{t('items')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {_.map(data?.items, (product) => (
                <Popconfirm
                  title={t('are_you_sure_remove_item_prompt')}
                  okText={t('yes')}
                  cancelText={t('no')}
                  onConfirm={() => removeItem(product.id)}
                >
                  <button
                    disabled
                    type="button"
                    key={product.id}
                    className="items-center border rounded-xl space-x-4 p-4"
                  >
                    <div className="flex items-center justify-center rounded-xl p-2">
                      <img
                        src={product?.businessItem?.item?.itemImage}
                        alt={`${product?.businessItem?.item?.brand} ${product?.businessItem?.item?.name} ${product?.businessItem?.item?.description}`}
                        className="object-center object-cover mx-auto w-20 h-20"
                      />
                    </div>
                    <div>
                      <div className="text-xs font-medium">{product?.businessItem?.item?.name}</div>
                      <div className="text-xs text-gray-500">{product?.businessItem?.item?.brand}</div>
                      <div className="text-xs text-gray-500">
                        {product?.businessItem?.item?.description}
                      </div>
                    </div>
                    <divider />
                    <div className="text-sm font-medium space-x-2 text-center">
                      <span>{toCurrency(product.price)}</span>
                      <span className="text-xs text-gray-600">x</span>
                      <span className="text-sm">{thousand(product.amount)}</span>
                    </div>
                  </button>
                </Popconfirm>
              ))}
            </div>
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('bins')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {_.map(data?.layawayBins, (bin) => (
                <Popconfirm
                  title={t('are_you_sure_freeup_bin_prompt')}
                  okText={t('yes')}
                  cancelText={t('no')}
                  onConfirm={() => freeupBin(bin.id)}
                >
                  <button
                    className="col-span-1 border-2 p-2 bg-yellow-600 border-yellow-600 bg-opacity-25 rounded-md"
                    type="button"
                  >
                    <p className="duration-200 text-center text-lg hover:font-bold hover:text-xl">{bin.tag}</p>
                    {bin.layaway && (
                      <Tag className="text-center">{bin.layaway?.code}</Tag>
                    )}
                  </button>
                </Popconfirm>
              ))}
            </div>
          </div>
          <div className="col-span-1 lg:col-span-3">
            <div className="flex">
              <span className="font-bold">{t('payments')}</span>
              <div className="ml-auto">
                <strong>{t('next_payment_on')}: </strong><span>{data?.nextExpirationDate ? renderShortDate(data?.nextExpirationDate, timezone) : '-'}</span>
              </div>
            </div>
            <Table
              borederd
              dataSource={data?.layawayPayments}
              columns={[
                {
                  title: '',
                  key: 'mobile',
                  align: 'center',
                  className: 'text-xs md:hidden',
                  render: (row) => (
                    <div className="grid grid-cols-2 gap-4 shadow-xl p-2">
                      <p className="col-span-2">{row?.order?.confirmationCode}</p>
                      <div className="col-span-1 text-left">
                        <p className="font-bold">{t('created_on')}</p>
                        <p>{renderShortDateTime(row?.createdAt, timezone)}</p>
                      </div>
                      <div className="col-span-1 text-right">
                        <p className="font-bold">{t('total')}</p>
                        <p>{toCurrency(row?.total)}</p>
                      </div>
                      <Button
                        className="col-span-2"
                        size="small"
                        type="primary"
                        loading={loadingOrder}
                        onClick={() => getOrder(row.orderId)}
                      >
                        {t('view')}
                      </Button>
                    </div>
                  ),
                },
                {
                  title: t('created_on'),
                  key: 'user',
                  align: 'center',
                  className: 'text-xs hidden md:table-cell ',
                  render: (row) => (
                    <span>{renderShortDateTime(row?.createdAt, timezone)}</span>
                  ),
                },
                {
                  title: t('order'),
                  key: 'order',
                  align: 'center',
                  className: 'text-xs hidden md:table-cell ',
                  render: (row) => (
                    <span>{row?.order?.confirmationCode}</span>
                  ),
                },
                {
                  title: t('total'),
                  key: 'total',
                  align: 'center',
                  className: 'text-xs hidden md:table-cell ',
                  render: (row) => (
                    <span>{toCurrency(row?.total)}</span>
                  ),
                },
                {
                  title: t('actions'),
                  key: 'actions',
                  align: 'center',
                  className: 'text-xs hidden md:table-cell ',
                  render: (row) => (
                    <Button
                      size="small"
                      type="primary"
                      loading={loadingOrder}
                      onClick={() => getOrder(row.orderId)}
                    >
                      {t('view')}
                    </Button>
                  ),
                },
              ]}
              size="small"
            />
          </div>
          <div className="col-span-1">
            <div className="flex">
              <p>{t('subtotal')}</p>
              <p className="ml-auto">{toCurrency(data?.subtotal)}</p>
            </div>
            <div className="flex">
              <p>{t('state_tax')}</p>
              <p className="ml-auto">{toCurrency(data?.stateTax)}</p>
            </div>
            {data?.reducedTax > 0 && (
              <div className="flex">
                <p>{t('reduced_tax')}</p>
                <p className="ml-auto">{toCurrency(data?.reducedTax)}</p>
              </div>
            )}
            <div className="flex">
              <p>{t('municipal_tax')}</p>
              <p className="ml-auto">{toCurrency(data?.municipalTax)}</p>
            </div>
            <div className="flex">
              <p>{t('service_fee')}</p>
              <p className="ml-auto">{toCurrency(data?.serviceFee)}</p>
            </div>
            <div className="flex">
              <p>{t('total')}</p>
              <p className="ml-auto font-bold">{toCurrency(data?.total)}</p>
            </div>

            <div className="flex mt-4">
              <p>{t('paid_amount')}</p>
              <p className="ml-auto font-bold">{toCurrency(getPaidAmount())}</p>
            </div>
            <div className="flex">
              <p>{t('payoff_balance')}</p>
              <p className="ml-auto font-bold">{toCurrency(getPayoffAmount())}</p>
            </div>
          </div>
        </div>
      </Modal>
      {/* <OrderDetailsModal clearOrderOnClose /> */}
    </>
  );
}

export default LayawayModal;
